/**
 * App Shell
 * CCH Care - Version 1.0
 * 
 * Main layout wrapper for authenticated pages.
 * Provides:
 * - Top header with user info, facility selector, logout
 * - Content area via <Outlet />
 * - Session timeout component
 */

import { Outlet } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';
import SessionTimeout from '../security/SessionTimeout';
import TrialBanner from '../ui/TrialBanner';
import { COLORS } from '../../config/constants';

export default function AppShell() {
  const { user, facilities, logout, switchFacility } = useAuth();

  const activeFacilityId = localStorage.getItem('care_facility_id');
  const activeFacility = facilities.find(f => String(f.facility_id) === String(activeFacilityId));

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg }}>
      <SessionTimeout />
      
      {/* Header */}
      <header style={{
        background: COLORS.primary,
        color: '#fff',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        {/* Brand + Facility */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>
              CCH Care
            </div>
            {activeFacility && (
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                {activeFacility.facility_name}
              </div>
            )}
          </div>
          
          {/* Facility selector (if multiple) */}
          {facilities.length > 1 && (
            <select
              value={activeFacilityId || ''}
              onChange={(e) => switchFacility(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              {facilities.map(f => (
                <option key={f.facility_id} value={f.facility_id} style={{ color: '#333' }}>
                  {f.facility_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* User info + Logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {user?.first_name} {user?.last_name}
            </div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {user?.role}
            </div>
          </div>
          <button
            onClick={logout}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.1)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Sign Out
          </button>
        </div>
      </header>

      {/* Trial Banner */}
      <TrialBanner />

      {/* Main Content */}
      <main style={{ maxWidth: 960, margin: '0 auto' }}>
        <Outlet />
      </main>
    </div>
  );
}
